// components/Prestamos.jsx
import React, { useState, useEffect, useCallback } from "react";
import MensajeValidacion from './MensajeValidacion';
import api from "../services/api";
import "./Prestamos.css";
import { formatearFecha, obtenerFechaLocal } from '../utils/dateUtils';

const Prestamos = () => {
  const [prestamos, setPrestamos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });
  const [modalAbierto, setModalAbierto] = useState(false);
  const [vistaActual, setVistaActual] = useState("todos");
  const [categorias, setCategorias] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [stockOrigen, setStockOrigen] = useState(null);
  const [modalDetalleLote, setModalDetalleLote] = useState(null);

  // ═══════════════════════════════════════════════════════════════
  // 🆕 ESTADO PARA PRÉSTAMOS EN LOTE
  // ═══════════════════════════════════════════════════════════════
  const [tipoPrestamo, setTipoPrestamo] = useState('individual');
  const [materialesLote, setMaterialesLote] = useState([]);
  const [materialTemporal, setMaterialTemporal] = useState({
    MaterialId: '',
    cantidad: ''
  });

  const [formData, setFormData] = useState({
    fecha_prestamo: obtenerFechaLocal(),
    ProyectoOrigenId: "",
    ProyectoDestinoId: "",
    MaterialId: "",
    cantidad: "",
    observacion: "",
  });

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vistaActual]);

  const cargarDatos = async () => {
    setLoading(true);

    let url = "/prestamos";
    if (vistaActual === "pendientes" || vistaActual === "alertas") {
      url = "/prestamos/pendientes";
    }

    const [prestRes, proyRes, matRes, catRes] = await Promise.allSettled([
      api.get(url),
      api.get("/proyectos/activos"),
      api.get("/materiales"),
      api.get("/categorias"),
    ]);

    if (prestRes.status === "fulfilled") {
      const d = prestRes.value.data;
      setPrestamos(d.prestamos || (Array.isArray(d) ? d : []));
    } else {
      console.error("Error al cargar préstamos:", prestRes.reason);
      setPrestamos([]);
    }

    if (proyRes.status === "fulfilled") {
      setProyectos(Array.isArray(proyRes.value.data) ? proyRes.value.data : []);
    } else {
      console.error("Error al cargar proyectos:", proyRes.reason);
    }

    if (matRes.status === "fulfilled") {
      setMateriales(Array.isArray(matRes.value.data) ? matRes.value.data : []);
    } else {
      console.error("Error al cargar materiales:", matRes.reason);
      setMateriales([]);
    }

    if (catRes.status === "fulfilled") {
      setCategorias(Array.isArray(catRes.value.data) ? catRes.value.data : []);
    } else {
      console.error("Error al cargar categorías:", catRes.reason);
    }

    setLoading(false);
  };

  const cargarMateriales = async () => {
    try {
      const res = await api.get("/materiales");
      setMateriales(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error al cargar materiales:", error);
      setMateriales([]);
    }
  };

  const verificarStockProyecto = useCallback(async () => {
    if (!formData.ProyectoOrigenId || !formData.MaterialId) {
      setStockOrigen(null);
      return;
    }

    try {
      const params = {
        ProyectoId: formData.ProyectoOrigenId,
        MaterialId: formData.MaterialId
      };

      const res = await api.get('/prestamos/stock-proyecto', { params });
      setStockOrigen(res.data);

      if (res.data.disponible) {
        console.log(`✅ Stock en proyecto origen: ${res.data.cantidad} ${res.data.material?.unidad || 'unidades'}`);
      } else {
        console.warn('⚠️ El proyecto origen no tiene stock de este material');
      }

    } catch (error) {
      console.error('Error al verificar stock de proyecto:', error);
      setStockOrigen(null);
    }
  }, [formData.ProyectoOrigenId, formData.MaterialId]);

  useEffect(() => {
    if (formData.ProyectoOrigenId && formData.MaterialId) {
      verificarStockProyecto();
    } else {
      setStockOrigen(null);
    }
  }, [formData.ProyectoOrigenId, formData.MaterialId, verificarStockProyecto]);

  // Recargar materiales cada vez que el modal se abre
  // (por si el carga inicial falló silenciosamente)
  useEffect(() => {
    if (modalAbierto) {
      cargarMateriales();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalAbierto]);

  const mostrarMensaje = (texto, tipo) => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
  };

  const abrirModal = () => {
    setFormData({
      fecha_prestamo: obtenerFechaLocal(),
      ProyectoOrigenId: "",
      ProyectoDestinoId: "",
      MaterialId: "",
      cantidad: "",
      observacion: "",
    });
    setTipoPrestamo('individual');
    setMaterialesLote([]);
    setMaterialTemporal({ MaterialId: '', cantidad: '' });
    setCategoriaFiltro("");
    setStockOrigen(null);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
  };

  // ═══════════════════════════════════════════════════════════════
  // 🆕 FUNCIONES PARA PRÉSTAMOS EN LOTE
  // ═══════════════════════════════════════════════════════════════
  
  const agregarMaterialALote = () => {
    if (!materialTemporal.MaterialId || !materialTemporal.cantidad) {
      mostrarMensaje("Seleccione un material y cantidad", "error");
      return;
    }

    // Verificar que no esté duplicado
    if (materialesLote.find(m => m.MaterialId === parseInt(materialTemporal.MaterialId))) {
      mostrarMensaje("Este material ya está en la lista", "error");
      return;
    }

    const material = materiales.find(m => m.id === parseInt(materialTemporal.MaterialId));
    
    // ✅ CORRECCIÓN: Convertir cantidad a número con precisión
    const cantidadNumerica = Number(materialTemporal.cantidad);
    
    setMaterialesLote([
      ...materialesLote,
      {
        MaterialId: parseInt(materialTemporal.MaterialId),
        cantidad: Number(cantidadNumerica.toFixed(2)), // ✅ Asegurar 2 decimales
        nombre: material.nombre,
        codigo: material.codigo
      }
    ]);

    // Limpiar campos
    setMaterialTemporal({ MaterialId: '', cantidad: '' });
  };

  const quitarMaterialDeLote = (MaterialId) => {
    setMaterialesLote(materialesLote.filter(m => m.MaterialId !== MaterialId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación de proyectos iguales
    if (formData.ProyectoOrigenId === formData.ProyectoDestinoId) {
      setMensaje({ 
        texto: "⚠️ PROYECTOS IGUALES",
        mensaje: "⚠️ PROYECTOS IGUALES",
        detalle: "El proyecto origen y destino deben ser diferentes. No puede prestarse a sí mismo.",
        tipo: "error" 
      });
      return;
    }

    // Preparar datos según tipo
    let datosEnvio = {
      tipo: tipoPrestamo,
      fecha_prestamo: formData.fecha_prestamo,
      ProyectoOrigenId: parseInt(formData.ProyectoOrigenId),
      ProyectoDestinoId: parseInt(formData.ProyectoDestinoId),
      observacion: formData.observacion
    };

    if (tipoPrestamo === 'individual') {
      if (!formData.MaterialId || !formData.cantidad) {
        mostrarMensaje("Debe especificar material y cantidad", "error");
        return;
      }
      datosEnvio.MaterialId = parseInt(formData.MaterialId);
      datosEnvio.cantidad = Number(formData.cantidad);
    } else {
      if (materialesLote.length === 0) {
        mostrarMensaje("Debe agregar al menos un material al lote", "error");
        return;
      }
      datosEnvio.materiales_lote = materialesLote;
    }

    try {
      const res = await api.post("/prestamos", datosEnvio);
      
      setMensaje({ 
        texto: res.data.texto || res.data.mensaje || `✅ Préstamo ${tipoPrestamo === 'lote' ? 'en lote' : ''} registrado correctamente`,
        mensaje: res.data.mensaje,
        detalle: res.data.detalle,
        codigo: res.data.codigo,
        cantidad: res.data.cantidad,
        cantidad_items: res.data.cantidad_items,
        proyecto_origen: res.data.proyecto_origen,
        proyecto_destino: res.data.proyecto_destino,
        material: res.data.material,
        tipo: "success" 
      });
      
      cerrarModal();
      cargarDatos();
      setStockOrigen(null);
      
    } catch (error) {
      console.error("Error al crear préstamo:", error);
      
      const errorData = error.response?.data;
      
      if (errorData) {
        setMensaje({
          texto: errorData.texto || errorData.mensaje || "❌ Error al crear préstamo",
          mensaje: errorData.mensaje,
          detalle: errorData.detalle,
          material: errorData.material,
          proyecto_origen: errorData.proyecto_origen,
          cantidad_disponible: errorData.cantidad_disponible,
          cantidad_solicitada: errorData.cantidad_solicitada,
          faltante: errorData.faltante,
          accion_requerida: errorData.accion_requerida,
          tipo: errorData.tipo || "error"
        });
      } else {
        setMensaje({
          texto: "❌ Error al crear préstamo",
          detalle: "Ocurrió un error inesperado. Por favor, intente nuevamente.",
          tipo: "error"
        });
      }
    }
  };

  const reponerPrestamo = async (id) => {
    if (!window.confirm("¿Confirmar reposición de este préstamo?")) return;

    try {
      await api.put(`/prestamos/reponer/${id}`);
      mostrarMensaje("✅ Préstamo repuesto correctamente", "success");
      cargarDatos();
    } catch (error) {
      console.error("Error al reponer:", error);
      mostrarMensaje(
        error.response?.data?.error || "Error al reponer préstamo",
        "error"
      );
    }
  };

  const cancelarPrestamo = async (id) => {
    if (!window.confirm("¿Cancelar este préstamo?")) return;

    try {
      await api.put(`/prestamos/cancelar/${id}`);
      mostrarMensaje("✅ Préstamo cancelado", "success");
      cargarDatos();
    } catch (error) {
      console.error("Error al cancelar:", error);
      mostrarMensaje(
        error.response?.data?.error || "Error al cancelar préstamo",
        "error"
      );
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: { texto: "⏳ Pendiente", clase: "badge-warning" },
      repuesto: { texto: "✅ Repuesto", clase: "badge-success" },
      cancelado: { texto: "❌ Cancelado", clase: "badge-danger" },
    };
    return badges[estado] || badges.pendiente;
  };

  const getAlertaBadge = (nivel) => {
    const badges = {
      NORMAL: { texto: "✓ Normal", clase: "badge-normal" },
      ADVERTENCIA: { texto: "⚠️ Advertencia", clase: "badge-advertencia" },
      URGENTE: { texto: "🔥 Urgente", clase: "badge-urgente" },
      CRÍTICO: { texto: "🚨 Crítico", clase: "badge-critico" },
    };
    return badges[nivel] || badges.NORMAL;
  };

  // Categorías derivadas de los materiales cargados (solo las que tienen materiales)
  const categoriasDisponibles = [
    ...new Map(
      materiales
        .filter(m => m.CategoriaId && m.categoria)
        .map(m => [m.CategoriaId, { id: m.CategoriaId, nombre: m.categoria.nombre }])
    ).values()
  ].sort((a, b) => a.nombre.localeCompare(b.nombre));

  const prestamosFiltrados = prestamos.filter((prest) => {
    if (vistaActual === "alertas") {
      return (
        prest.estado === "pendiente" &&
        ["ADVERTENCIA", "URGENTE", "CRÍTICO"].includes(prest.nivel_alerta)
      );
    }
    return true;
  });

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="prestamos-container">
      <div className="header-section">
        <h1>Gestión de Préstamos entre Proyectos</h1>
        <button className="btn-primary" onClick={abrirModal}>
          ➕ Nuevo Préstamo
        </button>
      </div>

      {mensaje.texto && <MensajeValidacion mensaje={mensaje} onClose={() => setMensaje({ texto: "", tipo: "" })} />}

      {/* Tabs de navegación */}
      <div className="tabs-navegacion">
        <button
          className={vistaActual === "todos" ? "tab active" : "tab"}
          onClick={() => setVistaActual("todos")}
        >
          📋 Todos
        </button>
        <button
          className={vistaActual === "pendientes" ? "tab active" : "tab"}
          onClick={() => setVistaActual("pendientes")}
        >
          ⏳ Pendientes
        </button>
        <button
          className={vistaActual === "alertas" ? "tab active" : "tab"}
          onClick={() => setVistaActual("alertas")}
        >
          🔔 Alertas
        </button>
      </div>

      {/* Tabla de préstamos */}
      <div className="table-container">
        <table className="prestamos-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Fecha</th>
              <th>Proyecto Origen</th>
              <th>Proyecto Destino</th>
              <th>Material</th>
              <th>Cantidad</th>
              <th>Valor Total</th>
              {(vistaActual === "pendientes" || vistaActual === "alertas") && (
                <th>Días</th>
              )}
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {prestamosFiltrados.map((prest) => {
              const badge = getEstadoBadge(prest.estado);
              const alertaBadge =
                prest.nivel_alerta && getAlertaBadge(prest.nivel_alerta);

              return (
                <tr key={prest.id}>
                  <td>
                    <strong>{prest.codigo}</strong>
                  </td>
                  <td>{formatearFecha(prest.fecha_prestamo)}</td>
                  <td>{prest.proyecto_origen?.nombre}</td>
                  <td>{prest.proyecto_destino?.nombre}</td>
                  <td>
                    {/* 🆕 NUEVO: Detectar si es lote */}
                    {prest.tipo === 'lote' ? (
                      <button
                        className="btn-ver-lote"
                        onClick={() => setModalDetalleLote(prest)}
                        style={{ 
                          fontWeight: 'bold', 
                          color: '#2563eb',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        📦 LOTE ({prest.materiales_lote?.length || 0} items)
                      </button>
                    ) : (
                      `${prest.material?.nombre || 'N/A'} ${prest.material?.codigo ? `(${prest.material.codigo})` : ''}`
                    )}
                  </td>
                  <td>
                    {prest.tipo === 'lote' ? (
                      <span style={{ fontStyle: 'italic', color: '#6b7280' }}>
                        Ver detalle
                      </span>
                    ) : (
                      <strong>{prest.cantidad}</strong>
                    )}
                  </td>
                  <td>RD$ {parseFloat(prest.valor_total || 0).toFixed(2)}</td>
                  {(vistaActual === "pendientes" ||
                    vistaActual === "alertas") && (
                    <td>
                      {prest.dias_transcurridos} días
                      {alertaBadge && (
                        <span className={`badge ${alertaBadge.clase}`}>
                          {alertaBadge.texto}
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    <span className={`badge ${badge.clase}`}>{badge.texto}</span>
                  </td>
                  <td className="acciones">
                    {prest.estado === "pendiente" && (
                      <>
                        <button
                          className="btn-mini btn-success"
                          onClick={() => reponerPrestamo(prest.id)}
                          title="Reponer"
                        >
                          ✓
                        </button>
                        <button
                          className="btn-mini btn-danger"
                          onClick={() => cancelarPrestamo(prest.id)}
                          title="Cancelar"
                        >
                          ✕
                        </button>
                      </>
                    )}
                    {prest.estado === "repuesto" &&
                      prest.fecha_reposicion && (
                        <span className="fecha-reposicion">
                          {formatearFecha(prest.fecha_reposicion)}
                        </span>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {prestamosFiltrados.length === 0 && (
          <div className="empty-state">
            <p>No hay préstamos registrados</p>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MODAL NUEVO PRÉSTAMO */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nuevo Préstamo</h2>
              <button className="btn-close" onClick={cerrarModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="info-box">
                <strong>ℹ️ Importante:</strong> El préstamo descuenta el
                material del proyecto origen y lo agrega al proyecto destino.
                El presupuesto de ambos proyectos se ajusta automáticamente.
              </div>

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* 🆕 TOGGLE: INDIVIDUAL / LOTE */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="tipo-transferencia-toggle" style={{ marginBottom: '20px' }}>
                <button
                  type="button"
                  className={tipoPrestamo === 'individual' ? 'active' : ''}
                  onClick={() => {
                    setTipoPrestamo('individual');
                    setMaterialesLote([]);
                    setMaterialTemporal({ MaterialId: '', cantidad: '' });
                  }}
                >
                  📦 Individual
                </button>
                <button
                  type="button"
                  className={tipoPrestamo === 'lote' ? 'active' : ''}
                  onClick={() => {
                    setTipoPrestamo('lote');
                    setFormData({ ...formData, MaterialId: '', cantidad: '' });
                  }}
                >
                  📦 Lote
                </button>
              </div>

              <div className="form-grid">
                {/* FECHA */}
                <div className="form-group">
                  <label>
                    Fecha <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.fecha_prestamo}
                    onChange={(e) =>
                      setFormData({ ...formData, fecha_prestamo: e.target.value })
                    }
                    required
                  />
                </div>

                {/* PROYECTO ORIGEN */}
                <div className="form-group">
                  <label>
                    Proyecto Origen (Prestador) <span className="required">*</span>
                  </label>
                  <select
                    value={formData.ProyectoOrigenId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        ProyectoOrigenId: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Seleccione...</option>
                    {proyectos.map((proy) => (
                      <option key={proy.id} value={proy.id}>
                        {proy.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* PROYECTO DESTINO */}
                <div className="form-group">
                  <label>
                    Proyecto Destino (Receptor) <span className="required">*</span>
                  </label>
                  <select
                    value={formData.ProyectoDestinoId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        ProyectoDestinoId: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Seleccione...</option>
                    {proyectos
                      .filter((proy) => proy.id !== parseInt(formData.ProyectoOrigenId))
                      .map((proy) => (
                        <option key={proy.id} value={proy.id}>
                          {proy.nombre}
                        </option>
                      ))}
                  </select>
                </div>
{/* ═══════════════════════════════════════════════════════════ */}
{/* MODO INDIVIDUAL */}
{/* ═══════════════════════════════════════════════════════════ */}
{tipoPrestamo === 'individual' && (
  <>
    {/* Filtro por Categoría */}
    <div className="form-group">
      <label>📁 Categoría (opcional)</label>
      <select
        value={categoriaFiltro}
        onChange={(e) => setCategoriaFiltro(e.target.value)}
      >
        <option value="">Todas las categorías</option>
        {categoriasDisponibles.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.nombre}
          </option>
        ))}
      </select>
    </div>

    {/* CONTENEDOR PARA MATERIAL Y CANTIDAD EN MISMA LÍNEA */}
    <div style={{ 
      display: 'flex', 
      gap: '1rem', 
      alignItems: 'flex-end',
      marginTop: '0.5rem'
    }}>
      {/* MATERIAL - Columna más ancha */}
      <div style={{ flex: 1 }}>
        <div className="form-group">
          <label>
            Material <span className="required">*</span>
          </label>
          <select
            value={formData.MaterialId}
            onChange={(e) => setFormData({ ...formData, MaterialId: e.target.value })}
            required
            style={{ width: '100%' }}
          >
            <option value="">
              {materiales.length === 0 ? "⚠️ Sin materiales disponibles" : "Seleccione..."}
            </option>
            {materiales
              .filter(m => categoriaFiltro ? m.CategoriaId === parseInt(categoriaFiltro) : true)
              .map((mat) => (
                <option key={mat.id} value={mat.id}>
                  {mat.nombre} {mat.codigo && `(${mat.codigo})`}
                </option>
              ))}
          </select>
          {materiales.length === 0 && (
            <small style={{ color: '#dc2626', fontSize: '12px' }}>
              No se encontraron materiales. Verifique que existan materiales registrados.
            </small>
          )}
        </div>
      </div>

      {/* CANTIDAD - Columna más angosta */}
      <div style={{ width: '150px' }}>
        <div className="form-group">
          <label>
            Cantidad <span className="required">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={formData.cantidad}
            onChange={(e) => {
              const valor = e.target.value;
              setFormData({ 
                ...formData, 
                cantidad: valor === '' ? '' : Number(valor)
              });
            }}
            onBlur={(e) => {
              if (e.target.value) {
                const valorFormateado = Number(e.target.value).toFixed(2);
                setFormData({ 
                  ...formData, 
                  cantidad: valorFormateado
                });
              }
            }}
            placeholder="0.00"
            required
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </div>

    {/* Stock disponible - Se mantiene igual pero con margen superior */}
    {stockOrigen && formData.ProyectoOrigenId && formData.MaterialId && (
      <div className="form-group full-width" style={{ marginTop: '1rem' }}>
        <div style={{
          padding: '12px',
          backgroundColor: stockOrigen.disponible ? '#f0fdf4' : '#fef2f2',
          border: `2px solid ${stockOrigen.disponible ? '#10b981' : '#ef4444'}`,
          borderRadius: '8px',
          fontSize: '13px'
        }}>
          {stockOrigen.disponible ? (
            <>
              <div style={{ fontWeight: 'bold', color: '#059669', marginBottom: '4px' }}>
                ✅ Stock disponible en proyecto origen: {stockOrigen.cantidad} {stockOrigen.material?.unidad || 'unidades'}
              </div>
              <div style={{ color: '#6b7280', fontSize: '12px' }}>
                📁 Proyecto: {stockOrigen.proyecto?.nombre}
              </div>
            </>
          ) : (
            <div style={{ fontWeight: 'bold', color: '#dc2626' }}>
              ⚠️ El proyecto origen no tiene stock de este material
            </div>
          )}
        </div>
      </div>
    )}
      
                  </>
                )}

               {/* MODO LOTE */}
{tipoPrestamo === 'lote' && (
  <div className="form-group full-width">
    <div className="lote-section">
      <h4>Agregar materiales al préstamo</h4>
      
      {/* Filtro Categoría (arriba) */}
      <div className="form-group">
        <label>📁 Categoría (Filtro)</label>
        <select
          value={categoriaFiltro}
          onChange={(e) => setCategoriaFiltro(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {categoriasDisponibles.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.nombre}
            </option>
          ))}
        </select>
      </div>

      {/* Grid para Material + Cantidad + Botón (igual que transferencias) */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        alignItems: 'flex-end',
        marginTop: '1rem'
      }}>
        {/* Columna Material */}
        <div style={{ flex: 1 }}>
          <div className="form-group">
            <label>Material</label>
            <select
              value={materialTemporal.MaterialId}
              onChange={(e) =>
                setMaterialTemporal({ ...materialTemporal, MaterialId: e.target.value })
              }
              style={{ width: '100%' }}
            >
              <option value="">
                {materiales.length === 0 ? "⚠️ Sin materiales disponibles" : "Seleccione..."}
              </option>
              {materiales
                .filter(m =>
                  categoriaFiltro ? m.CategoriaId === parseInt(categoriaFiltro) : true
                )
                .filter(m =>
                  !materialesLote.find(ml => ml.MaterialId === m.id)
                )
                .map((mat) => (
                  <option key={mat.id} value={mat.id}>
                    {mat.codigo} - {mat.nombre}
                  </option>
                ))}
            </select>
            {materiales.length === 0 && (
              <small style={{ color: '#dc2626', fontSize: '12px' }}>
                No se encontraron materiales. Verifique que existan materiales registrados.
              </small>
            )}
          </div>
        </div>

        {/* Columna Cantidad */}
        <div style={{ width: '120px' }}>
          <div className="form-group">
            <label>Cantidad</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={materialTemporal.cantidad}
              onChange={(e) => {
                const valor = e.target.value;
                setMaterialTemporal({ 
                  ...materialTemporal, 
                  cantidad: valor === '' ? '' : Number(valor)
                });
              }}
              onBlur={(e) => {
                if (e.target.value) {
                  const valorFormateado = Number(e.target.value).toFixed(2);
                  setMaterialTemporal({ 
                    ...materialTemporal, 
                    cantidad: valorFormateado
                  });
                }
              }}
              placeholder="0.00"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Botón */}
        <div style={{ width: '140px' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={agregarMaterialALote}
            style={{ 
              height: '46px', 
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ➕ Agregar
          </button>
        </div>
      </div>
    </div>

                    {/* Lista de Materiales Agregados */}
                    {materialesLote.length > 0 && (
                      <div className="materiales-lote-lista" style={{ marginTop: '20px' }}>
                        <h4>Materiales agregados ({materialesLote.length}):</h4>
                        <table className="tabla-materiales-lote">
                          <thead>
                            <tr>
                              <th>Material</th>
                              <th>Código</th>
                              <th>Cantidad</th>
                              <th>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materialesLote.map((item) => (
                              <tr key={item.MaterialId}>
                                <td>{item.nombre}</td>
                                <td>{item.codigo}</td>
                                <td><strong>{item.cantidad}</strong></td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn-mini btn-danger"
                                    onClick={() => quitarMaterialDeLote(item.MaterialId)}
                                    title="Quitar"
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {materialesLote.length === 0 && (
                      <div className="sin-materiales-lote" style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        color: '#6b7280',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        marginTop: '10px'
                      }}>
                        ℹ️ Agregue al menos un material para crear el préstamo en lote
                      </div>
                    )}
                  </div>
                )}

                {/* OBSERVACIONES */}
                <div className="form-group full-width">
                  <label>Observación</label>
                  <textarea
                    value={formData.observacion}
                    onChange={(e) =>
                      setFormData({ ...formData, observacion: e.target.value })
                    }
                    placeholder="Motivo o comentarios del préstamo"
                    rows="3"
                  />
                </div>
              </div>

              {/* BOTONES */}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={cerrarModal}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  💾 Registrar Préstamo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 🆕 MODAL DETALLE LOTE */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {modalDetalleLote && (
        <div className="modal-overlay" onClick={() => setModalDetalleLote(null)}>
          <div className="modal-content modal-detalle-lote" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📦 Detalle de Préstamo en Lote</h2>
              <button className="btn-close" onClick={() => setModalDetalleLote(null)}>
                ✕
              </button>
            </div>

            <div className="detalle-lote-info">
              <p><strong>Código:</strong> {modalDetalleLote.codigo}</p>
              <p><strong>Fecha:</strong> {formatearFecha(modalDetalleLote.fecha_prestamo)}</p>
              <p><strong>Origen:</strong> {modalDetalleLote.proyecto_origen?.nombre}</p>
              <p><strong>Destino:</strong> {modalDetalleLote.proyecto_destino?.nombre}</p>
              <p><strong>Estado:</strong> <span className={`badge ${getEstadoBadge(modalDetalleLote.estado).clase}`}>
                {getEstadoBadge(modalDetalleLote.estado).texto}
              </span></p>
            </div>

            <h3>Materiales Prestados:</h3>
            <table className="tabla-materiales-lote">
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Código</th>
                  <th>Cantidad</th>
                  <th>Costo Unit.</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {modalDetalleLote.materiales_lote?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.nombre}</td>
                    <td>{item.codigo}</td>
                    <td><strong>{item.cantidad}</strong></td>
                    <td>RD$ {item.costo_unitario ? parseFloat(item.costo_unitario).toFixed(2) : '0.00'}</td>
                    <td><strong>RD$ {item.valor_linea ? parseFloat(item.valor_linea).toFixed(2) : '0.00'}</strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" style={{ textAlign: 'right', fontWeight: 'bold' }}>TOTAL:</td>
                  <td><strong>RD$ {modalDetalleLote.valor_total ? parseFloat(modalDetalleLote.valor_total).toFixed(2) : '0.00'}</strong></td>
                </tr>
              </tfoot>
            </table>

            {modalDetalleLote.observacion && (
              <div className="observacion-lote" style={{ 
                marginTop: '15px', 
                padding: '10px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '6px' 
              }}>
                <strong>Observación:</strong> {modalDetalleLote.observacion}
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalDetalleLote(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prestamos;